import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import AudioRecorder from '../components/AudioRecorder';
import AudioPlayer from '../components/AudioPlayer';
import './TryRecChord.css';

// Mock Auth Context for guest users - prevents real auth from being triggered
const MockAuthContext = createContext();

function MockAuthProvider({ children }) {
  const value = {
    user: { tier: 'free', email: 'guest@rec-chord.com' },
    isAuthenticated: false,
    hasActiveSubscription: false,
    login: async () => {},
    register: async () => {},
    logout: async () => {},
    googleLogin: async () => {},
    handleGoogleCallback: async () => {},
    validateToken: async () => {},
    isLoading: false,
    error: null
  };

  return (
    <MockAuthContext.Provider value={value}>
      {children}
    </MockAuthContext.Provider>
  );
}

// Override useAuth for this page
export function useAuth() {
  const context = useContext(MockAuthContext);
  if (context) return context;
  // Fallback for when not wrapped in MockAuthProvider
  return {
    user: null,
    isAuthenticated: false,
    hasActiveSubscription: false,
    isLoading: false,
    error: null
  };
}

// Simple transcript display component for guests
function SimpleTranscriptDisplay({ data, currentTime }) {
  const words = useMemo(() => {
    if (!data || !data.segments) return [];
    const allWords = [];
    data.segments.forEach((segment) => {
      if (segment.words) {
        segment.words.forEach((word) => {
          allWords.push(word);
        });
      }
    });
    return allWords;
  }, [data]);

  const currentWordIndex = useMemo(() => {
    if (!words.length) return -1;
    for (let i = words.length - 1; i >= 0; i--) {
      if (words[i].start <= currentTime) {
        return i;
      }
    }
    return -1;
  }, [words, currentTime]);

  if (!data || !words.length) {
    return <div className="simple-transcript-empty">No transcript available</div>;
  }

  return (
    <div className="simple-transcript">
      <div className="simple-transcript-content">
        {words.map((word, index) => (
          <span
            key={index}
            className={`simple-word ${index === currentWordIndex ? 'current' : ''}`}
          >
            {word.word}{' '}
          </span>
        ))}
      </div>
    </div>
  );
}

function TryRecChordContent() {
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [guestToken, setGuestToken] = useState(null);

  const audioRef = useRef(null);
  const autoTranscribeTriggered = useRef(false);

  // Handle recording complete
  const handleRecordingComplete = useCallback((file, timestamp) => {
    console.log('[TryRecChord] Recording complete:', file.name);
    setAudioFile(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
  }, []);

  // Auto-transcribe when audio is ready
  useEffect(() => {
    if (audioFile && !transcriptData && !isLoading && !autoTranscribeTriggered.current) {
      autoTranscribeTriggered.current = true;
      handleTranscribe();
    }
  }, [audioFile, transcriptData, isLoading]);

  // Handle transcription
  const handleTranscribe = async () => {
    if (!audioFile) return;

    setIsLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('audio', audioFile);

    try {
      const response = await fetch('/transcribe-async', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Transcription failed');
      }

      const data = await response.json();
      const jobId = data.jobId;

      // Store guest token in state
      if (data.guestToken) {
        setGuestToken(data.guestToken);
        console.log('[TryRecChord] Guest token stored in state');
      }

      const tokenForPolling = data.guestToken;

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/jobs/${jobId}`, {
            headers: tokenForPolling ? { 'Authorization': `Bearer ${tokenForPolling}` } : {}
          });
          const jobStatus = await statusResponse.json();

          if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);
            setTranscriptData(jobStatus.result);
            setIsLoading(false);
          } else if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            setError(jobStatus.error || 'Transcription failed');
            setIsLoading(false);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setError('Failed to check transcription status');
          setIsLoading(false);
        }
      }, 2000);

    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Handle login prompt for save/export
  const handleLoginPrompt = (action) => {
    window.location.href = `/auth?redirect=/try&action=${action}`;
  };

  // Get plain text from transcript
  const getTranscriptText = () => {
    if (!transcriptData || !transcriptData.segments) return '';
    return transcriptData.segments
      .map(seg => seg.words ? seg.words.map(w => w.word).join(' ') : seg.text || '')
      .join(' ');
  };

  // Copy transcript text
  const handleCopy = async () => {
    const text = getTranscriptText();
    try {
      await navigator.clipboard.writeText(text);
      alert('Transcript copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="try-recchord-page">
      {/* Header */}
      <header className="try-header">
        <div className="try-header-left">
          <Link to="/" className="try-logo">
            <div className="logo-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
            </div>
            <span>Rec-Chord</span>
          </Link>
        </div>

        <div className="try-header-center">
          <span className="try-badge">Try Rec-Chord</span>
        </div>

        <div className="try-header-right">
          <Link to="/contact" className="try-contact-link">
            Contact Us
          </Link>
          <Link to="/auth" className="try-signin-btn">
            Sign In
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="try-main">
        {!audioUrl && !transcriptData && (
          <div className="try-intro">
            <h1>Try Rec-Chord Free</h1>
            <p>Record audio and get it transcribed instantly. No sign-up required.</p>

            <div className="try-recorder-section">
              <AudioRecorder
                onRecordingComplete={handleRecordingComplete}
                isAuthenticated={true}
                hasTranscript={false}
                userTier="free"
              />
            </div>

            <div className="try-features">
              <div className="try-feature">
                <span className="feature-icon">🎙️</span>
                <span>Record Audio</span>
              </div>
              <div className="try-feature">
                <span className="feature-icon">📝</span>
                <span>Auto Transcribe</span>
              </div>
              <div className="try-feature">
                <span className="feature-icon">✏️</span>
                <span>Edit Text</span>
              </div>
            </div>

            <p className="try-signin-note">
              Sign in to save, export, and access your transcriptions anytime.
            </p>
          </div>
        )}

        {isLoading && (
          <div className="try-loading">
            <div className="loading-spinner"></div>
            <p>Transcribing your audio...</p>
          </div>
        )}

        {error && (
          <div className="try-error">
            <p>{error}</p>
            <button onClick={() => { setError(null); autoTranscribeTriggered.current = false; }}>
              Try Again
            </button>
          </div>
        )}

        {transcriptData && !isLoading && (
          <div className="try-transcript-section">
            {/* Audio Player */}
            {audioUrl && (
              <div className="try-audio-player">
                <AudioPlayer
                  ref={audioRef}
                  src={audioUrl}
                  onTimeUpdate={setCurrentTime}
                />
              </div>
            )}

            {/* Simple Transcript Display */}
            <div className="try-editor">
              <SimpleTranscriptDisplay
                data={transcriptData}
                currentTime={currentTime}
              />
            </div>

            {/* Action Buttons */}
            <div className="try-actions">
              <button
                className="try-action-btn copy-btn"
                onClick={handleCopy}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
                Copy Text
              </button>

              <button
                className="try-action-btn save-btn"
                onClick={() => handleLoginPrompt('save')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
                Save
              </button>

              <button
                className="try-action-btn export-btn"
                onClick={() => handleLoginPrompt('export')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export
              </button>
            </div>

            <div className="try-signin-prompt">
              <p>Want to save your work?</p>
              <Link to="/auth" className="signin-link">Sign in or create an account</Link>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="try-footer">
        <p>
          <Link to="/contact">Contact Us</Link> for questions about signing in or using Rec-Chord
        </p>
      </footer>
    </div>
  );
}

// Main component wrapped with MockAuthProvider
function TryRecChord() {
  return (
    <MockAuthProvider>
      <TryRecChordContent />
    </MockAuthProvider>
  );
}

export default TryRecChord;
